import React, { useState } from 'react';
import { Share2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface ShareFormButtonProps {
  formType: 'alta-cliente-cra' | 'mandato-sepa';
  title: string;
  description: string;
}

export function ShareFormButton({ formType, title, description }: ShareFormButtonProps) {
  const { toast } = useToast();
  const { perfil } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Include empresa_id so public submissions are linked to the correct company
  const empresaParam = perfil?.empresa_id ? `?empresa_id=${perfil.empresa_id}` : '';
  const url = `${window.location.origin}/${formType}${empresaParam}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Enlace copiado",
        description: "El enlace del formulario ha sido copiado al portapapeles",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo copiar el enlace",
        variant: "destructive",
      });
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url,
        });
      } catch (err) {
        console.log("Error al compartir:", err);
      }
    } else {
      copyLink();
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2"
        >
          <Share2 className="w-4 h-4" />
          Compartir formulario
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Comparte este enlace para que puedan acceder al formulario:
            </p>
            <Input
              value={url}
              readOnly
              className="mb-2"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={copyLink} variant="outline" className="flex-1">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copiado' : 'Copiar enlace'}
            </Button>
            <Button onClick={shareLink} className="flex-1">
              <Share2 className="w-4 h-4 mr-2" />
              Compartir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}